/* ============================================
   SCROLL STORY — Scroll Engine
   ============================================ */

(function () {
  'use strict';

  class ScrollStory {
    constructor(el) {
      this.el = el;
      this.track = el.querySelector('.scroll-story__track');
      this.stage = el.querySelector('.scroll-story__stage');
      this.debugEl = el.querySelector('.scroll-story__debug');
      this.blocks = [];
      this.progress = 0;
      this.rafId = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._initBlocks();
      this._bindEvents();
      this._tick();
    }

    _initBlocks() {
      const blockEls = this.stage.querySelectorAll('.scroll-story__block');

      blockEls.forEach((el) => {
        const type = el.dataset.blockType;
        const scrollIn = parseFloat(el.dataset.scrollIn) / 100;
        const scrollOut = parseFloat(el.dataset.scrollOut) / 100;
        const animation = el.dataset.animation;
        const zIndex = el.dataset.zIndex || 1;

        el.style.zIndex = zIndex;

        const block = { el, type, scrollIn, scrollOut, animation };

        // VIDEO block
        if (type === 'video') {
          const video = el.querySelector('.scroll-story__video');
          const scrub = el.dataset.scrubOnScroll === 'true';
          block.video = video;
          block.scrub = scrub;

          if (video && video.tagName === 'VIDEO') {
            if (!scrub) {
              video.setAttribute('autoplay', '');
              video.setAttribute('loop', '');
              video.muted = true;
              video.play().catch(() => {});

              // Pause when out of viewport, play when in
              const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                  if (entry.isIntersecting) {
                    video.play().catch(() => {});
                  } else {
                    video.pause();
                  }
                });
              }, { threshold: 0.1 });
              observer.observe(el);
            } else {
              video.pause();
              video.preload = 'auto';
            }
          }
        }

        // IMAGE SEQUENCE block
        if (type === 'sequence') {
          const canvas = el.querySelector('canvas');
          const frames = JSON.parse(el.dataset.frames || '[]');
          block.canvas = canvas;
          block.ctx = canvas ? canvas.getContext('2d') : null;
          block.frames = frames;
          block.images = [];
          block.loadedCount = 0;

          if (canvas) {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
          }

          frames.forEach((src, i) => {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              block.loadedCount++;
              if (i === 0) this._drawFrame(block, img);
            };
            block.images.push(img);
          });
        }

        // CAROUSEL block
        if (type === 'carousel') {
          const track = el.querySelector('.scroll-story__carousel-track');
          const items = el.querySelectorAll('.scroll-story__carousel-item');
          const itemsPerView = parseInt(el.dataset.itemsPerView) || 4;
          const itemsPerViewMobile = parseInt(el.dataset.itemsPerViewMobile) || 2;
          const gap = parseInt(el.dataset.gap) || 16;
          const introPortion = parseFloat(el.dataset.introPortion) / 100 || 0.4;

          block.carouselTrack = track;
          block.carouselItems = items;
          block.itemsPerView = itemsPerView;
          block.itemsPerViewMobile = itemsPerViewMobile;
          block.gap = gap;
          block.introPortion = introPortion;
        }

        this.blocks.push(block);
      });
    }

    _bindEvents() {
      window.addEventListener('scroll', () => {}, { passive: true });

      window.addEventListener('resize', () => {
        this.blocks.forEach((b) => {
          if (b.type === 'sequence' && b.canvas) {
            b.canvas.width = window.innerWidth;
            b.canvas.height = window.innerHeight;
          }
        });
      });
    }

    _getProgress() {
      const rect = this.track.getBoundingClientRect();
      const trackHeight = this.track.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      return Math.min(Math.max(scrolled / trackHeight, 0), 1);
    }

    _lerp(a, b, t) {
      return a + (b - a) * t;
    }

    _mapRange(value, inMin, inMax, outMin, outMax) {
      const t = Math.min(Math.max((value - inMin) / (inMax - inMin), 0), 1);
      return this._lerp(outMin, outMax, t);
    }

    _applyAnimation(block, localProgress) {
      const { el, animation } = block;

      if (this.reducedMotion) {
        el.style.opacity = 1;
        el.style.transform = 'none';
        return;
      }

      let opacity = 1;
      let translateX = 0;
      let translateY = 0;
      let scale = 1;

      // Fade in over first 20% of block window, fade out over last 20%
      const fadeIn = Math.min(localProgress / 0.2, 1);
      const fadeOut = localProgress > 0.8 ? 1 - (localProgress - 0.8) / 0.2 : 1;
      opacity = Math.min(fadeIn, fadeOut);

      switch (animation) {
        case 'fade':
          break;

        case 'slide-up':
          translateY = this._mapRange(localProgress, 0, 0.2, 60, 0);
          if (localProgress > 0.8) translateY = this._mapRange(localProgress, 0.8, 1, 0, -60);
          break;

        case 'slide-down':
          translateY = this._mapRange(localProgress, 0, 0.2, -60, 0);
          if (localProgress > 0.8) translateY = this._mapRange(localProgress, 0.8, 1, 0, 60);
          break;

        case 'slide-left':
          translateX = this._mapRange(localProgress, 0, 0.2, 80, 0);
          if (localProgress > 0.8) translateX = this._mapRange(localProgress, 0.8, 1, 0, -80);
          break;

        case 'slide-right':
          translateX = this._mapRange(localProgress, 0, 0.2, -80, 0);
          if (localProgress > 0.8) translateX = this._mapRange(localProgress, 0.8, 1, 0, 80);
          break;

        case 'scale':
          scale = this._mapRange(localProgress, 0, 0.2, 0.8, 1);
          if (localProgress > 0.8) scale = this._mapRange(localProgress, 0.8, 1, 1, 0.8);
          break;

        case 'parallax':
          translateY = this._mapRange(localProgress, 0, 1, 40, -40);
          opacity = 1;
          break;

        case 'none':
            opacity = 1;
            el.style.opacity = 1;
            el.style.transform = 'none';
            return; // exit early, don't let anything below override it
      }

      el.style.opacity = opacity;
      el.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
    }

    _applyCarousel(block, localProgress) {
      const { carouselTrack, carouselItems, gap } = block;
      if (!carouselTrack || carouselItems.length === 0) return;

      const isMobile = window.innerWidth <= 749;
      const itemsPerView = isMobile ? block.itemsPerViewMobile : block.itemsPerView;
      const totalItems = carouselItems.length;
      const introPortion = block.introPortion; // 0–1

      // Phase 1: intro — reveal items one by one
      if (localProgress <= introPortion) {
        const introProgress = localProgress / introPortion; // 0→1 within intro
        const itemsToShow = Math.min(
          Math.ceil(introProgress * itemsPerView),
          itemsPerView
        );

        carouselItems.forEach((item, i) => {
          item.style.opacity = i < itemsToShow ? 1 : 0;
        });

        // No track movement during intro
        carouselTrack.style.transform = 'translateX(0px)';
        return;
      }

      // Phase 2: scroll-drive — all intro items visible, scroll remaining
      carouselItems.forEach((item, i) => {
        item.style.opacity = 1;
      });

      const scrollableItems = Math.max(0, totalItems - itemsPerView);
      if (scrollableItems === 0) return;

      const scrollProgress = (localProgress - introPortion) / (1 - introPortion); // 0→1 within scroll phase
      const itemWidth = carouselItems[0].offsetWidth;
      const maxOffset = scrollableItems * (itemWidth + gap);
      const offset = -(scrollProgress * maxOffset);

      carouselTrack.style.transform = `translateX(${offset}px)`;
    }

    _drawFrame(block, img) {
      const { ctx, canvas } = block;
      if (!ctx || !canvas) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const scale = Math.max(canvas.width / img.width, canvas.height / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      const x = (canvas.width - w) / 2;
      const y = (canvas.height - h) / 2;
      ctx.drawImage(img, x, y, w, h);
    }

    _tick() {
      this.rafId = requestAnimationFrame(() => this._tick());

      const progress = this._getProgress();
      this.progress = progress;

      if (this.debugEl) {
        this.debugEl.textContent = `scroll: ${(progress * 100).toFixed(1)}%`;
      }

      this.blocks.forEach((block) => {
        const { scrollIn, scrollOut } = block;
        const isVisible = progress >= scrollIn && progress <= scrollOut;

        const currentOpacity = parseFloat(block.el.style.opacity) || 0;
        block.el.classList.toggle('is-active', isVisible || currentOpacity > 0.1);

        if (isVisible) {
            const localProgress = (progress - scrollIn) / (scrollOut - scrollIn);

            this._applyAnimation(block, localProgress);

            // Video scrubbing
            if (block.type === 'video' && block.scrub && block.video && block.video.tagName === 'VIDEO') {
                const duration = block.video.duration;
                if (duration && !isNaN(duration)) {
                block.video.currentTime = localProgress * duration;
                }
            }

            // Image sequence
            if (block.type === 'sequence' && block.images.length > 0) {
                const frameIndex = Math.min(
                Math.floor(localProgress * block.images.length),
                block.images.length - 1
                );
                const img = block.images[frameIndex];
                if (img && img.complete) {
                this._drawFrame(block, img);
                }
            }

            // Carousel scroll-drive
            if (block.type === 'carousel') {
                this._applyCarousel(block, localProgress);
            }

            } else {
            // Only hide if not reduced motion
            if (!this.reducedMotion) {
                block.el.style.opacity = 0;
                block.el.style.transform = '';
            }
        }
      });
    }

    destroy() {
      cancelAnimationFrame(this.rafId);
    }
  }

  // Init all scroll story sections on the page
  document.querySelectorAll('.scroll-story').forEach((el) => {
    new ScrollStory(el);
  });

})();
